import { transformError as sdkTransformError } from '@wix/sdk-runtime/transform-error';
import {
  renameKeysFromSDKRequestToRESTRequest,
  renameKeysFromRESTResponseToSDKResponse,
} from '@wix/sdk-runtime/rename-all-nested-keys';
import { HttpClient, NonNullablePaths } from '@wix/sdk-types';
import * as ambassadorWixBookingsV2BookingsSettings from './bookings-v2-bookings-settings-bookings-settings.http.js';

/**
 * Site-wide settings that configure how the Wix Bookings app behaves across all of a
 * site's services and booking flows.
 *
 * Each site has a single bookings settings object. It's created automatically with
 * default values, so a site always has bookings settings.
 */
export interface BookingsSettings {
  /**
   * Revision number, which increments by 1 each time the bookings settings are updated.
   * To prevent conflicting changes,
   * the current revision must be passed when updating the bookings settings.
   * @readonly
   */
  revision?: string | null;
  /**
   * Date and time the bookings settings were created.
   * @readonly
   */
  _createdDate?: Date | null;
  /**
   * Date and time the bookings settings were updated.
   * @readonly
   */
  _updatedDate?: Date | null;
  /** Cart checkout settings, which control whether customers can add multiple services to a cart and check out together. */
  cartCheckout?: CartCheckout;
  /** Multi-service appointment settings, which control whether customers can book more than one service in a single appointment. */
  multiServiceAppointments?: MultiServiceAppointments;
  /** Display time zone settings, which control whether times are shown in the business's or the customer's time zone. */
  displayTimeZone?: DisplayTimeZone;
  /**
   * Custom field data for the bookings settings object.
   *
   * [Extended fields](https://dev.wix.com/docs/build-apps/develop-your-app/extensions/backend-extensions/schema-plugins/about-schema-plugin-extensions) must be configured in the app dashboard before they can be accessed with API calls.
   */
  extendedFields?: ExtendedFields;
  /**
   * Notifications settings
   * @internal
   */
  notifications?: Notifications;
  /**
   * Staff selection settings, which control when and how customers choose a staff member during the booking flow.
   * @internal
   */
  staffSelection?: StaffSelection;
  /**
   * Location selection settings, which control when customers choose a service location during the booking flow.
   * @internal
   */
  locationSelection?: LocationSelection;
}

export interface CartCheckout {
  /** Whether customers can add multiple services to a cart and check out together. */
  enabled?: boolean | null;
}

export interface MultiServiceAppointments {
  /** Whether customers can book more than one service in a single appointment. */
  enabled?: boolean | null;
}

export interface DisplayTimeZone {
  /** Whether customers can switch between the business and customer time zones when viewing times. */
  customerCanChange?: boolean | null;
  /** Time zone that times are displayed in by default. */
  basedOn?: BasedOnWithLiterals;
}

export enum BasedOn {
  /** Times are displayed in the business's time zone. */
  BUSINESS_TIME_ZONE = 'BUSINESS_TIME_ZONE',
  /** Times are displayed in the customer's time zone. */
  CUSTOMER_TIME_ZONE = 'CUSTOMER_TIME_ZONE',
}

/** @enumType */
export type BasedOnWithLiterals =
  | BasedOn
  | 'BUSINESS_TIME_ZONE'
  | 'CUSTOMER_TIME_ZONE';

export interface SiteProperties {
  /** Selected locale. */
  locale?: Locale;
  /**
   * Selected language code (ISO 639-1).
   * @format LANGUAGE
   */
  language?: string | null;
  /**
   * Selected currency code (ISO 4217) to bill customers of this site.
   * @format CURRENCY
   */
  paymentCurrency?: string | null;
  /**
   * Time zone.
   * @maxLength 255
   */
  timeZone?: string | null;
  /**
   * Publicly-available business email address.
   * @format EMAIL
   */
  email?: string | null;
  /**
   * Publicly-available business phone number.
   * @format PHONE
   */
  phone?: string | null;
  /** Publicly-available business address. */
  address?: Address;
  /**
   * Site display name.
   * @maxLength 255
   */
  siteDisplayName?: string | null;
  /**
   * Business name.
   * @maxLength 255
   */
  businessName?: string | null;
}

export interface Locale {
  /**
   * Language code (ISO 639-1).
   * @format LANGUAGE
   */
  languageCode?: string;
  /**
   * Country code (ISO 3166-1).
   * @format COUNTRY
   */
  country?: string;
}

export interface Address {
  /**
   * Street name.
   * @maxLength 255
   */
  street?: string;
  /**
   * City.
   * @maxLength 255
   */
  city?: string;
  /**
   * Country code (2 alphanumeric characters per ISO 3166-1).
   * @maxLength 255
   */
  country?: string;
  /**
   * State (if applicable).
   * @maxLength 255
   */
  state?: string;
  /**
   * Zip code.
   * @maxLength 255
   */
  zip?: string;
  /** Extra information to be displayed in the address. */
  hint?: AddressHint;
  /** Whether this address represents a physical location. */
  physical?: boolean;
  /**
   * A Google-formatted version of this address (acceptable to most APIs).
   * @maxLength 255
   */
  googleFormattedAddress?: string;
  /**
   * Street number.
   * @maxLength 255
   */
  streetNumber?: string;
  /**
   * Apartment number (if applicable).
   * @maxLength 255
   */
  apartmentNumber?: string;
  /** Geocoordinates. */
  coordinates?: GeoCoordinates;
}

/**
 * Extra information on displayed addresses.
 * This is used for display purposes. Used to add additional data about the address, such as "In the passage".
 * Free text. In addition the user can state where he wants that additional description - before, after, or instead
 * the address string.
 */
export interface AddressHint {
  /**
   * Extra text displayed next to, or instead of, the actual address.
   * @maxLength 255
   */
  text?: string;
  /** Where the extra text should be displayed. */
  placement?: PlacementTypeWithLiterals;
}

/** Where the extra text should be displayed: before, after or instead of the actual address. */
export enum PlacementType {
  BEFORE = 'BEFORE',
  AFTER = 'AFTER',
  REPLACE = 'REPLACE',
}

/** @enumType */
export type PlacementTypeWithLiterals =
  | PlacementType
  | 'BEFORE'
  | 'AFTER'
  | 'REPLACE';

/** Geocoordinates for a particular address. */
export interface GeoCoordinates {
  /** Latitude. */
  latitude?: number;
  /** Longitude. */
  longitude?: number;
}

export interface Features {
  /** `bookings_service_list` feature */
  bookingsServiceList?: Feature;
  /** `bookings_payments` feature */
  bookingsPayments?: Feature;
  /** `bookings_groups` feature */
  bookingsGroups?: Feature;
  /** `bookings_reminders` feature */
  bookingsReminders?: Feature;
  /** `bookings_courses` feature */
  bookingsCourses?: Feature;
  /** `bookings_appointments` feature */
  bookingsAppointments?: Feature;
  /** `bookings_sms_reminders` feature */
  bookingsSmsReminders?: Feature;
  /** `bookings_external_calendars` feature */
  bookingsExternalCalendars?: Feature;
  /** `bookings_staff_limit` feature */
  bookingsStaffLimit?: Feature;
  /** `anywhere_bookings_service_limit` feature */
  anywhereBookingsServiceLimit?: Feature;
  /** `anywhere_bookings_staff_limit` feature */
  anywhereBookingsStaffLimit?: Feature;
  /** `anywhere_remove_brand` feature */
  anywhereRemoveBrand?: Feature;
  /** `anywhere_customization_widget` feature */
  anywhereCustomizationWidget?: Feature;
  /** `bookings_video_conferencing` feature */
  bookingsVideoConferencing?: Feature;
  /** `accept_payments` feature */
  acceptPayments?: Feature;
}

export interface Feature {
  /** true if feature is enabled */
  enabled?: boolean;
  /** limit for the feature quota. null if no limit exists for the feature */
  quotaLimit?: string | null;
}

export interface ExtendedFields {
  /**
   * Extended field data. Each key corresponds to the namespace of the app that created the extended fields.
   * The value of each key is structured according to the schema defined when the extended fields were configured.
   *
   * You can only access fields for which you have the appropriate permissions.
   *
   * Learn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).
   */
  namespaces?: Record<string, Record<string, any>>;
}

export interface Notifications {
  /** Whether to include payment links in automated booking notifications (confirmations, reminders, etc.). */
  includePaymentLinks?: boolean | null;
}

export interface StaffSelection {
  /** When customers choose a staff member during the booking flow. */
  timing?: StaffSelectionTimingWithLiterals;
  /** How the staff member for a booking is selected. */
  strategy?: StrategyWithLiterals;
}

export enum StaffSelectionTiming {
  /** Customers choose a staff member before selecting a time slot. */
  BEFORE_SLOT_SELECTION = 'BEFORE_SLOT_SELECTION',
  /** Customers choose a staff member after selecting a time slot. */
  AFTER_SLOT_SELECTION = 'AFTER_SLOT_SELECTION',
}

/** @enumType */
export type StaffSelectionTimingWithLiterals =
  | StaffSelectionTiming
  | 'BEFORE_SLOT_SELECTION'
  | 'AFTER_SLOT_SELECTION';

export enum Strategy {
  /** Customers must choose a staff member. */
  CUSTOMER_MUST_CHOOSE = 'CUSTOMER_MUST_CHOOSE',
  /** Customers can choose a staff member but aren't required to. If they don't, an available staff member is assigned automatically. */
  CUSTOMER_MAY_CHOOSE = 'CUSTOMER_MAY_CHOOSE',
  /** Customers can't choose a staff member. An available staff member is assigned automatically. */
  CUSTOMER_CANNOT_CHOOSE = 'CUSTOMER_CANNOT_CHOOSE',
}

/** @enumType */
export type StrategyWithLiterals =
  | Strategy
  | 'CUSTOMER_MUST_CHOOSE'
  | 'CUSTOMER_MAY_CHOOSE'
  | 'CUSTOMER_CANNOT_CHOOSE';

export interface LocationSelection {
  /** When customers choose a service location during the booking flow. */
  timing?: TimingWithLiterals;
}

export enum Timing {
  /** Customers choose a location before selecting a time slot. */
  BEFORE_SLOT_SELECTION = 'BEFORE_SLOT_SELECTION',
  /** Customers choose a location after selecting a time slot. */
  AFTER_SLOT_SELECTION = 'AFTER_SLOT_SELECTION',
}

/** @enumType */
export type TimingWithLiterals =
  | Timing
  | 'BEFORE_SLOT_SELECTION'
  | 'AFTER_SLOT_SELECTION';

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at `16:00` and then again at `16:01`, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entity?: string;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntity?: string;
}

export interface EntityDeletedEvent {
  /** Entity that was deleted. */
  deletedEntity?: string | null;
}

export interface ActionEvent {
  body?: string;
}

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

/** Owner booking experience settings. */
export interface OwnerFlow {
  /** Booking creation settings. */
  bookingCreation?: BookingCreationSettings;
}

/** Settings that control the behavior of the owner booking creation flow in the calendar. */
export interface BookingCreationSettings {
  requiredFields?: RequiredFieldWithLiterals[];
}

export enum RequiredField {
  PHONE = 'PHONE',
  EMAIL = 'EMAIL',
}

/** @enumType */
export type RequiredFieldWithLiterals = RequiredField | 'PHONE' | 'EMAIL';

export interface GetBookingsSettingsRequest {}

export enum RequestedFields {
  SITE_PROPERTIES = 'SITE_PROPERTIES',
  FEATURES = 'FEATURES',
  OWNER_FLOW = 'OWNER_FLOW',
}

/** @enumType */
export type RequestedFieldsWithLiterals =
  | RequestedFields
  | 'SITE_PROPERTIES'
  | 'FEATURES'
  | 'OWNER_FLOW';

export interface GetBookingsSettingsResponse {
  /** Retrieved bookings settings. */
  bookingsSettings?: BookingsSettings;
}

export interface UpdateBookingsSettingsRequest {
  /** Bookings settings to update. */
  bookingsSettings: BookingsSettings;
}

export interface UpdateBookingsSettingsResponse {
  /** Updated bookings settings. */
  bookingsSettings?: BookingsSettings;
}

export interface MetaSiteSpecialEvent extends MetaSiteSpecialEventPayloadOneOf {
  /** Emitted on a meta site creation. */
  siteCreated?: SiteCreated;
  /** Emitted on a meta site transfer completion. */
  siteTransferred?: SiteTransferred;
  /** Emitted on a meta site deletion. */
  siteDeleted?: SiteDeleted;
  /** Emitted on a meta site restoration. */
  siteUndeleted?: SiteUndeleted;
  /** Emitted on the first* publish of the meta site (* switching from unpublished to published state). */
  sitePublished?: SitePublished;
  /** Emitted on a meta site unpublish. */
  siteUnpublished?: SiteUnpublished;
  /** Emitted when meta site is marked as template. */
  siteMarkedAsTemplate?: SiteMarkedAsTemplate;
  /** Emitted when meta site is marked as a WixSite. */
  siteMarkedAsWixSite?: SiteMarkedAsWixSite;
  /** Emitted when an application is provisioned (installed). */
  serviceProvisioned?: ServiceProvisioned;
  /** Emitted when an application is removed (uninstalled). */
  serviceRemoved?: ServiceRemoved;
  /** Emitted when meta site name (URL slug) is changed. */
  siteRenamedPayload?: SiteRenamed;
  /** Emitted when meta site was permanently deleted. */
  hardDeleted?: SiteHardDeleted;
  /** Emitted on a namespace change. */
  namespaceChanged?: NamespaceChanged;
  /** Emitted when Studio is attached. */
  studioAssigned?: StudioAssigned;
  /** Emitted when Studio is detached. */
  studioUnassigned?: StudioUnassigned;
  /**
   * Emitted when one of the URLs is changed. After this event you may call `urls-server` to fetch
   * the actual URL.
   *
   * See: https://wix.slack.com/archives/C0UHEBPFT/p1732520791210559?thread_ts=1732027914.294059&cid=C0UHEBPFT
   * See: https://wix.slack.com/archives/C0UHEBPFT/p1744115197619459
   */
  urlChanged?: SiteUrlChanged;
  /** Site is marked as PurgedExternally */
  sitePurgedExternally?: SitePurgedExternally;
  /** Emitted when Odeditor is attached. */
  odeditorAssigned?: OdeditorAssigned;
  /** Emitted when Odeditor is detached. */
  odeditorUnassigned?: OdeditorUnassigned;
  /** Emitted when Picasso is attached. */
  picassoAssigned?: PicassoAssigned;
  /** Emitted when Picasso is detached. */
  picassoUnassigned?: PicassoUnassigned;
  /** Emitted when Wixel is attached. */
  wixelAssigned?: WixelAssigned;
  /** Emitted when Wixel is detached. */
  wixelUnassigned?: WixelUnassigned;
  /** Emitted when StudioTwo is attached. */
  studioTwoAssigned?: StudioTwoAssigned;
  /** Emitted when StudioTwo is detached. */
  studioTwoUnassigned?: StudioTwoUnassigned;
  /** Emitted when media from user domain is enabled. */
  userDomainMediaEnabled?: UserDomainMediaEnabled;
  /** Emitted when media from user domain is disabled. */
  userDomainMediaDisabled?: UserDomainMediaDisabled;
  /** Emitted when Editorless is attached. */
  editorlessAssigned?: EditorlessAssigned;
  /** Emitted when Editorless is detached. */
  editorlessUnassigned?: EditorlessUnassigned;
  /**
   * A meta site id.
   * @format GUID
   */
  metaSiteId?: string;
  /** A meta site version. Monotonically increasing. */
  version?: string;
  /** A timestamp of the event. */
  timestamp?: string;
  /**
   * TODO(meta-site): Change validation once validations are disabled for consumers
   * More context: https://wix.slack.com/archives/C0UHEBPFT/p1720957844413149 and https://wix.slack.com/archives/CFWKX325T/p1728892152855659
   * @maxSize 4000
   */
  assets?: Asset[];
}

/** @oneof */
export interface MetaSiteSpecialEventPayloadOneOf {
  /** Emitted on a meta site creation. */
  siteCreated?: SiteCreated;
  /** Emitted on a meta site transfer completion. */
  siteTransferred?: SiteTransferred;
  /** Emitted on a meta site deletion. */
  siteDeleted?: SiteDeleted;
  /** Emitted on a meta site restoration. */
  siteUndeleted?: SiteUndeleted;
  /** Emitted on the first* publish of the meta site (* switching from unpublished to published state). */
  sitePublished?: SitePublished;
  /** Emitted on a meta site unpublish. */
  siteUnpublished?: SiteUnpublished;
  /** Emitted when meta site is marked as template. */
  siteMarkedAsTemplate?: SiteMarkedAsTemplate;
  /** Emitted when meta site is marked as a WixSite. */
  siteMarkedAsWixSite?: SiteMarkedAsWixSite;
  /** Emitted when an application is provisioned (installed). */
  serviceProvisioned?: ServiceProvisioned;
  /** Emitted when an application is removed (uninstalled). */
  serviceRemoved?: ServiceRemoved;
  /** Emitted when meta site name (URL slug) is changed. */
  siteRenamedPayload?: SiteRenamed;
  /** Emitted when meta site was permanently deleted. */
  hardDeleted?: SiteHardDeleted;
  /** Emitted on a namespace change. */
  namespaceChanged?: NamespaceChanged;
  /** Emitted when Studio is attached. */
  studioAssigned?: StudioAssigned;
  /** Emitted when Studio is detached. */
  studioUnassigned?: StudioUnassigned;
  /**
   * Emitted when one of the URLs is changed. After this event you may call `urls-server` to fetch
   * the actual URL.
   *
   * See: https://wix.slack.com/archives/C0UHEBPFT/p1732520791210559?thread_ts=1732027914.294059&cid=C0UHEBPFT
   * See: https://wix.slack.com/archives/C0UHEBPFT/p1744115197619459
   */
  urlChanged?: SiteUrlChanged;
  /** Site is marked as PurgedExternally */
  sitePurgedExternally?: SitePurgedExternally;
  /** Emitted when Odeditor is attached. */
  odeditorAssigned?: OdeditorAssigned;
  /** Emitted when Odeditor is detached. */
  odeditorUnassigned?: OdeditorUnassigned;
  /** Emitted when Picasso is attached. */
  picassoAssigned?: PicassoAssigned;
  /** Emitted when Picasso is detached. */
  picassoUnassigned?: PicassoUnassigned;
  /** Emitted when Wixel is attached. */
  wixelAssigned?: WixelAssigned;
  /** Emitted when Wixel is detached. */
  wixelUnassigned?: WixelUnassigned;
  /** Emitted when StudioTwo is attached. */
  studioTwoAssigned?: StudioTwoAssigned;
  /** Emitted when StudioTwo is detached. */
  studioTwoUnassigned?: StudioTwoUnassigned;
  /** Emitted when media from user domain is enabled. */
  userDomainMediaEnabled?: UserDomainMediaEnabled;
  /** Emitted when media from user domain is disabled. */
  userDomainMediaDisabled?: UserDomainMediaDisabled;
  /** Emitted when Editorless is attached. */
  editorlessAssigned?: EditorlessAssigned;
  /** Emitted when Editorless is detached. */
  editorlessUnassigned?: EditorlessUnassigned;
}

export interface Asset {
  /**
   * An application definition id (app_id in dev-center). For legacy reasons may be UUID or a string (from Java Enum).
   * @maxLength 36
   */
  appDefId?: string;
  /**
   * An instance id. For legacy reasons may be UUID or a string.
   * @maxLength 200
   */
  instanceId?: string;
  /** An application state. */
  state?: StateWithLiterals;
}

export enum State {
  UNKNOWN = 'UNKNOWN',
  ENABLED = 'ENABLED',
  DISABLED = 'DISABLED',
  PENDING = 'PENDING',
  DEMO = 'DEMO',
}

/** @enumType */
export type StateWithLiterals =
  | State
  | 'UNKNOWN'
  | 'ENABLED'
  | 'DISABLED'
  | 'PENDING'
  | 'DEMO';

export interface SiteCreated {
  /**
   * A template identifier (empty if not created from a template).
   * @maxLength 36
   */
  originTemplateId?: string;
  /**
   * An account id of the owner.
   * @format GUID
   */
  ownerId?: string;
  /** A context in which meta site was created. */
  context?: SiteCreatedContextWithLiterals;
  /**
   * A meta site id from which this site was created.
   *
   * In case of a creation from a template it's a template id.
   * In case of a site duplication ("Save As" in dashboard or duplicate in UM) it's an id of a source site.
   * @format GUID
   */
  originMetaSiteId?: string | null;
  /**
   * A meta site name (URL slug).
   * @maxLength 20
   */
  siteName?: string;
  /** A namespace. */
  namespace?: NamespaceWithLiterals;
}

export enum SiteCreatedContext {
  /** A valid option, we don't expose all reasons why site might be created. */
  OTHER = 'OTHER',
  /** A meta site was created from template. */
  FROM_TEMPLATE = 'FROM_TEMPLATE',
  /** A meta site was created by copying of the transfferred meta site. */
  DUPLICATE_BY_SITE_TRANSFER = 'DUPLICATE_BY_SITE_TRANSFER',
  /** A copy of existing meta site. */
  DUPLICATE = 'DUPLICATE',
  /** A meta site was created as a transfferred site (copy of the original), old flow, should die soon. */
  OLD_SITE_TRANSFER = 'OLD_SITE_TRANSFER',
  /** deprecated A meta site was created for Flash editor. */
  FLASH = 'FLASH',
}

/** @enumType */
export type SiteCreatedContextWithLiterals =
  | SiteCreatedContext
  | 'OTHER'
  | 'FROM_TEMPLATE'
  | 'DUPLICATE_BY_SITE_TRANSFER'
  | 'DUPLICATE'
  | 'OLD_SITE_TRANSFER'
  | 'FLASH';

export enum Namespace {
  UNKNOWN_NAMESPACE = 'UNKNOWN_NAMESPACE',
  /** Default namespace for UGC sites. MetaSites with this namespace will be shown in a user's site list by default. */
  WIX = 'WIX',
  /** ShoutOut stand alone product. These are siteless (no actual Wix site, no HtmlWeb). MetaSites with this namespace will *not* be shown in a user's site list by default. */
  SHOUT_OUT = 'SHOUT_OUT',
  /** MetaSites created by the Albums product, they appear as part of the Albums app. MetaSites with this namespace will *not* be shown in a user's site list by default. */
  ALBUMS = 'ALBUMS',
  /** Part of the WixStores migration flow, a user tries to migrate and gets this site to view and if the user likes it then stores removes this namespace and deletes the old site with the old stores. MetaSites with this namespace will *not* be shown in a user's site list by default. */
  WIX_STORES_TEST_DRIVE = 'WIX_STORES_TEST_DRIVE',
  /** Hotels standalone (siteless). MetaSites with this namespace will *not* be shown in a user's site list by default. */
  HOTELS = 'HOTELS',
  /** Clubs siteless MetaSites, a club without a wix website. MetaSites with this namespace will *not* be shown in a user's site list by default. */
  CLUBS = 'CLUBS',
  /** A partially created ADI website. MetaSites with this namespace will *not* be shown in a user's site list by default. */
  ONBOARDING_DRAFT = 'ONBOARDING_DRAFT',
  /** AppBuilder for AppStudio / shmite (c). MetaSites with this namespace will *not* be shown in a user's site list by default. */
  DEV_SITE = 'DEV_SITE',
  /** LogoMaker websites offered to the user after logo purchase. MetaSites with this namespace will *not* be shown in a user's site list by default. */
  LOGOS = 'LOGOS',
  /** VideoMaker websites offered to the user after video purchase. MetaSites with this namespace will *not* be shown in a user's site list by default. */
  VIDEO_MAKER = 'VIDEO_MAKER',
  /** MetaSites with this namespace will *not* be shown in a user's site list by default. */
  PARTNER_DASHBOARD = 'PARTNER_DASHBOARD',
  /** MetaSites with this namespace will *not* be shown in a user's site list by default. */
  DEV_CENTER_COMPANY = 'DEV_CENTER_COMPANY',
  /**
   * A draft created by HTML editor on open. Upon "first save" it will be moved to be of WIX domain.
   *
   * Meta site with this namespace will *not* be shown in a user's site list by default.
   */
  HTML_DRAFT = 'HTML_DRAFT',
  /**
   * the user-journey for Fitness users who want to start from managing their business instead of designing their website.
   * Will be accessible from Site List and will not have a website app.
   * Once the user attaches a site, the site will become a regular wixsite.
   */
  SITELESS_BUSINESS = 'SITELESS_BUSINESS',
  /** Belongs to "strategic products" company. Supports new product in the creator's economy space. */
  CREATOR_ECONOMY = 'CREATOR_ECONOMY',
  /** It is to be used in the Business First efforts. */
  DASHBOARD_FIRST = 'DASHBOARD_FIRST',
  /** Bookings business flow with no site. */
  ANYWHERE = 'ANYWHERE',
  /** Namespace for Headless Backoffice with no editor */
  HEADLESS = 'HEADLESS',
  /**
   * Namespace for master site that will exist in parent account that will be referenced by subaccounts
   * The site will be used for account level CSM feature for enterprise
   */
  ACCOUNT_MASTER_CMS = 'ACCOUNT_MASTER_CMS',
  /** Rise.ai Siteless account management for Gift Cards and Store Credit. */
  RISE = 'RISE',
  /**
   * As part of the branded app new funnel, users now can create a meta site that will be branded app first.
   * There's a blank site behind the scene but it's blank).
   * The Mobile company will be the owner of this namespace.
   */
  BRANDED_FIRST = 'BRANDED_FIRST',
  /** Nownia.com Siteless account management for Ai Scheduling Assistant. */
  NOWNIA = 'NOWNIA',
  /**
   * UGC Templates are templates that are created by users for personal use and to sale to other users.
   * The Partners company owns this namespace.
   */
  UGC_TEMPLATE = 'UGC_TEMPLATE',
  /** Codux Headless Sites */
  CODUX = 'CODUX',
  /** Bobb - AI Design Creator. */
  MEDIA_DESIGN_CREATOR = 'MEDIA_DESIGN_CREATOR',
  /**
   * Shared Blog Site is a unique single site across Enterprise account,
   * This site will hold all Blog posts related to the Marketing product.
   */
  SHARED_BLOG_ENTERPRISE = 'SHARED_BLOG_ENTERPRISE',
  /** Standalone forms (siteless). MetaSites with this namespace will *not* be shown in a user's site list by default. */
  STANDALONE_FORMS = 'STANDALONE_FORMS',
  /** Standalone events (siteless). MetaSites with this namespace will *not* be shown in a user's site list by default. */
  STANDALONE_EVENTS = 'STANDALONE_EVENTS',
  /** MIMIR - Siteless account for MIMIR Ai Job runner. */
  MIMIR = 'MIMIR',
  /** Wix Twins platform. */
  TWINS = 'TWINS',
  /** Wix Nano. */
  NANO = 'NANO',
  /** Base44 headless sites. */
  BASE44 = 'BASE44',
  /** Wix Channels Sites */
  CHANNELS = 'CHANNELS',
  /** Nautilus platform. */
  NAUTILUS = 'NAUTILUS',
  /**
   * Symphony — a siteless site representing a project within Symphony (the evolution of Orion).
   * Holds project data, conversations, assets, and manages collaborators / shared team members for the project.
   */
  SYMPHONY = 'SYMPHONY',
  /** Nautilus platform app. */
  NAUTILUS_APPS = 'NAUTILUS_APPS',
  /**
   * Vibu — a siteless site representing a project built with Vibu (the AI app builder).
   * Holds the project's generated code, conversations, assets, and manages collaborators for the project.
   */
  VIBU = 'VIBU',
  /**
   * Ghost site for the BrandKit multi-site platform. Each ghost backs a single BrandKit
   * (an Enterprise account may have several), holding that BrandKit's brand definition
   * (theme, typography, assets) which is rolled out to the BrandKit's connected sites.
   * MetaSites with this namespace will *not* be shown in a user's site list by default.
   */
  ENTERPRISE_BRAND_KIT = 'ENTERPRISE_BRAND_KIT',
}

/** @enumType */
export type NamespaceWithLiterals =
  | Namespace
  | 'UNKNOWN_NAMESPACE'
  | 'WIX'
  | 'SHOUT_OUT'
  | 'ALBUMS'
  | 'WIX_STORES_TEST_DRIVE'
  | 'HOTELS'
  | 'CLUBS'
  | 'ONBOARDING_DRAFT'
  | 'DEV_SITE'
  | 'LOGOS'
  | 'VIDEO_MAKER'
  | 'PARTNER_DASHBOARD'
  | 'DEV_CENTER_COMPANY'
  | 'HTML_DRAFT'
  | 'SITELESS_BUSINESS'
  | 'CREATOR_ECONOMY'
  | 'DASHBOARD_FIRST'
  | 'ANYWHERE'
  | 'HEADLESS'
  | 'ACCOUNT_MASTER_CMS'
  | 'RISE'
  | 'BRANDED_FIRST'
  | 'NOWNIA'
  | 'UGC_TEMPLATE'
  | 'CODUX'
  | 'MEDIA_DESIGN_CREATOR'
  | 'SHARED_BLOG_ENTERPRISE'
  | 'STANDALONE_FORMS'
  | 'STANDALONE_EVENTS'
  | 'MIMIR'
  | 'TWINS'
  | 'NANO'
  | 'BASE44'
  | 'CHANNELS'
  | 'NAUTILUS'
  | 'SYMPHONY'
  | 'NAUTILUS_APPS'
  | 'VIBU'
  | 'ENTERPRISE_BRAND_KIT';

/** Site transferred to another user. */
export interface SiteTransferred {
  /**
   * A previous owner id (user that transfers meta site).
   * @format GUID
   */
  oldOwnerId?: string;
  /**
   * A new owner id (user that accepts meta site).
   * @format GUID
   */
  newOwnerId?: string;
}

/** Soft deletion of the meta site. Could be restored. */
export interface SiteDeleted {
  /** A deletion context. */
  deleteContext?: DeleteContext;
}

export interface DeleteContext {
  /** When the meta site was deleted. */
  dateDeleted?: Date | null;
  /** A status. */
  deleteStatus?: DeleteStatusWithLiterals;
  /**
   * A reason (flow).
   * @maxLength 255
   */
  deleteOrigin?: string;
  /**
   * A service that deleted it.
   * @maxLength 255
   */
  initiatorId?: string | null;
}

export enum DeleteStatus {
  UNKNOWN = 'UNKNOWN',
  TRASH = 'TRASH',
  DELETED = 'DELETED',
  PENDING_PURGE = 'PENDING_PURGE',
  PURGED_EXTERNALLY = 'PURGED_EXTERNALLY',
}

/** @enumType */
export type DeleteStatusWithLiterals =
  | DeleteStatus
  | 'UNKNOWN'
  | 'TRASH'
  | 'DELETED'
  | 'PENDING_PURGE'
  | 'PURGED_EXTERNALLY';

/** Restoration of the meta site. */
export interface SiteUndeleted {}

/** First publish of a meta site. Or subsequent publish after unpublish. */
export interface SitePublished {}

export interface SiteUnpublished {
  /**
   * A list of URLs previously associated with the meta site.
   * @maxLength 4000
   * @maxSize 10000
   */
  urls?: string[];
}

export interface SiteMarkedAsTemplate {}

export interface SiteMarkedAsWixSite {}

/**
 * Represents a service provisioned a site.
 *
 * Note on `origin_instance_id`:
 * There is no guarantee that you will be able to find a meta site using `origin_instance_id`.
 * This is because of the following scenario:
 *
 * Imagine you have a template where a third-party application (TPA) includes some stub data,
 * such as a product catalog. When you create a site from this template, you inherit this
 * default product catalog. However, if the template's product catalog is modified,
 * your site will retain the catalog as it was at the time of site creation. This ensures that
 * your site remains consistent with what you initially received and does not include any
 * changes made to the original template afterward.
 * To ensure this, the TPA on the template gets a new instance_id.
 */
export interface ServiceProvisioned {
  /**
   * Either UUID or EmbeddedServiceType.
   * @maxLength 36
   */
  appDefId?: string;
  /**
   * Not only UUID. Something here could be something weird.
   * @maxLength 36
   */
  instanceId?: string;
  /**
   * An instance id from which this instance is originated.
   * @maxLength 36
   */
  originInstanceId?: string;
  /**
   * A version.
   * @maxLength 500
   */
  version?: string | null;
  /**
   * The origin meta site id
   * @format GUID
   */
  originMetaSiteId?: string | null;
}

export interface ServiceRemoved {
  /**
   * Either UUID or EmbeddedServiceType.
   * @maxLength 36
   */
  appDefId?: string;
  /**
   * Not only UUID. Something here could be something weird.
   * @maxLength 36
   */
  instanceId?: string;
  /**
   * A version.
   * @maxLength 500
   */
  version?: string | null;
}

/** Rename of the site. Meaning, free public url has been changed as well. */
export interface SiteRenamed {
  /**
   * A new meta site name (URL slug).
   * @maxLength 20
   */
  newSiteName?: string;
  /**
   * A previous meta site name (URL slug).
   * @maxLength 255
   */
  oldSiteName?: string;
}

/**
 * Hard deletion of the meta site.
 *
 * Could not be restored. Therefore it's desirable to cleanup data.
 */
export interface SiteHardDeleted {
  /** A deletion context. */
  deleteContext?: DeleteContext;
}

export interface NamespaceChanged {
  /** A previous namespace. */
  oldNamespace?: NamespaceWithLiterals;
  /** A new namespace. */
  newNamespace?: NamespaceWithLiterals;
}

/** Assigned Studio editor */
export interface StudioAssigned {}

/** Unassigned Studio editor */
export interface StudioUnassigned {}

/**
 * Fired in case site URLs were changed in any way: new secondary domain, published, account slug rename, site rename etc.
 *
 * This is an internal event, it's not propagated in special events, because it's non-actionable. If you need to keep up
 * with sites and its urls, you need to listen to another topic/event. Read about it:
 *
 * https://bo.wix.com/wix-docs/rest/meta-site/meta-site---urls-service
 */
export interface SiteUrlChanged {}

/**
 * Used at the end of the deletion flow for both draft sites and when a user deletes a site.
 * Consumed by other teams to remove relevant data.
 */
export interface SitePurgedExternally {
  /**
   * @maxLength 2048
   * @maxSize 100
   * @deprecated
   * @targetRemovalDate 2025-04-15
   */
  appDefId?: string[];
}

/** Assigned Odeditor */
export interface OdeditorAssigned {}

/** Unassigned Odeditor */
export interface OdeditorUnassigned {}

/** Assigned Picasso editor */
export interface PicassoAssigned {}

/** Unassigned Picasso */
export interface PicassoUnassigned {}

/** Assigned Wixel */
export interface WixelAssigned {}

/** Unassigned Wixel */
export interface WixelUnassigned {}

/** Assigned StudioTwo */
export interface StudioTwoAssigned {}

/** Unassigned StudioTwo */
export interface StudioTwoUnassigned {}

/** Media from user domain is enabled. */
export interface UserDomainMediaEnabled {}

/** Media from user domain is disabled. */
export interface UserDomainMediaDisabled {}

/** Assigned Editorless */
export interface EditorlessAssigned {}

/** Unassigned Editorless */
export interface EditorlessUnassigned {}

export interface Empty {}

export interface BusinessNotification {
  reason?: ReasonWithLiterals;
}

export enum Reason {
  UNKNOWN = 'UNKNOWN',
  CUSTOM_PROPERTIES_UPDATED = 'CUSTOM_PROPERTIES_UPDATED',
  ONBOARDING_STATUS_UPDATED = 'ONBOARDING_STATUS_UPDATED',
}

/** @enumType */
export type ReasonWithLiterals =
  | Reason
  | 'UNKNOWN'
  | 'CUSTOM_PROPERTIES_UPDATED'
  | 'ONBOARDING_STATUS_UPDATED';

export interface AccountInfo {
  /**
   * ID of the Wix account associated with the event.
   * @format GUID
   */
  accountId?: string | null;
  /**
   * ID of the parent Wix account. Only included when accountId belongs to a child account.
   * @format GUID
   */
  parentAccountId?: string | null;
  /**
   * ID of the Wix site associated with the event. Only included when the event is tied to a specific site.
   * @format GUID
   */
  siteId?: string | null;
}

/** @docsIgnore */
export type GetBookingsSettingsApplicationErrors = {
  code?: 'BOOKINGS_SETTINGS_NOT_FOUND';
  description?: string;
  data?: Record<string, any>;
};

export interface BaseEventMetadata {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface EventMetadata extends BaseEventMetadata {
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at `16:00` and then again at `16:01`, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
  accountInfo?: AccountInfoMetadata;
}

export interface AccountInfoMetadata {
  /** ID of the Wix account associated with the event */
  accountId: string;
  /** ID of the Wix site associated with the event. Only included when the event is tied to a specific site. */
  siteId?: string;
  /** ID of the parent Wix account. Only included when 'accountId' belongs to a child account. */
  parentAccountId?: string;
}

export interface BookingsSettingsCreatedEnvelope {
  entity: BookingsSettings;
  metadata: EventMetadata;
}

/**
 * (explanation)
 *
 * Events are an integral part of your API and should be designed alongside all other methods that your domain exposes.
 * Remember - events are the only way a client can react to your domain's business flow without constantly polling your
 * API for changes.
 *
 * Therefore you MUST expose all lifecycle events even if you don't have a use case in your own business flows that
 * consumes them.
 *
 * In order to simplify clients ability to consume events in a coherent way, we have defined a common standard for
 * sending events from platformized services related to your domain (hence DomainEvents). This is both inside Wix and
 * externally (webhooks and velo).
 *
 * Refer to [AIP 7016 - Events and Hooks][1] to learn more about callback annotation.
 * Callback is an event, which is fired as a byproduct of calling this method.
 *
 * [1]: https://dev.wix.com/docs/rnd-general/articles/p13n-guidelines-aips/guidance-aips/design-patterns/7016-events
 * @permissionScope Manage Stores
 * @permissionScopeId SCOPE.STORES.MANAGE-STORES
 * @permissionScope Manage Bookings Services and Settings
 * @permissionScopeId SCOPE.BOOKINGS.CONFIGURATION
 * @permissionScope Manage Bookings
 * @permissionScopeId SCOPE.DC-BOOKINGS.MANAGE-BOOKINGS
 * @permissionScope Manage Bookings - all permissions
 * @permissionScopeId SCOPE.DC-BOOKINGS-MEGA.MANAGE-BOOKINGS
 * @permissionId BOOKINGS.SETTINGS_READ
 * @webhook
 * @eventType wix.bookings.v2.bookings_settings_created
 * @slug created
 * @documentationMaturity preview
 */
export declare function onBookingsSettingsCreated(
  handler: (event: BookingsSettingsCreatedEnvelope) => void | Promise<void>
): void;

export interface BookingsSettingsDeletedEnvelope {
  entity: BookingsSettings;
  metadata: EventMetadata;
}

/** @permissionScope Manage Stores
 * @permissionScopeId SCOPE.STORES.MANAGE-STORES
 * @permissionScope Manage Bookings Services and Settings
 * @permissionScopeId SCOPE.BOOKINGS.CONFIGURATION
 * @permissionScope Manage Bookings
 * @permissionScopeId SCOPE.DC-BOOKINGS.MANAGE-BOOKINGS
 * @permissionScope Manage Bookings - all permissions
 * @permissionScopeId SCOPE.DC-BOOKINGS-MEGA.MANAGE-BOOKINGS
 * @permissionId BOOKINGS.SETTINGS_READ
 * @webhook
 * @eventType wix.bookings.v2.bookings_settings_deleted
 * @slug deleted
 * @documentationMaturity preview
 */
export declare function onBookingsSettingsDeleted(
  handler: (event: BookingsSettingsDeletedEnvelope) => void | Promise<void>
): void;

export interface BookingsSettingsUpdatedEnvelope {
  entity: BookingsSettings;
  metadata: EventMetadata;
  /** @hidden */
  modifiedFields: Record<string, any>;
}

/** @permissionScope Manage Stores
 * @permissionScopeId SCOPE.STORES.MANAGE-STORES
 * @permissionScope Manage Bookings Services and Settings
 * @permissionScopeId SCOPE.BOOKINGS.CONFIGURATION
 * @permissionScope Manage Bookings
 * @permissionScopeId SCOPE.DC-BOOKINGS.MANAGE-BOOKINGS
 * @permissionScope Manage Bookings - all permissions
 * @permissionScopeId SCOPE.DC-BOOKINGS-MEGA.MANAGE-BOOKINGS
 * @permissionId BOOKINGS.SETTINGS_READ
 * @webhook
 * @eventType wix.bookings.v2.bookings_settings_updated
 * @slug updated
 * @documentationMaturity preview
 */
export declare function onBookingsSettingsUpdated(
  handler: (event: BookingsSettingsUpdatedEnvelope) => void | Promise<void>
): void;

/**
 * Retrieves the site's bookings settings.
 *
 * If no settings have been customized for the site yet, the default settings are returned.
 * @public
 * @documentationMaturity preview
 * @permissionId BOOKINGS.SETTINGS_READ
 * @applicableIdentity APP
 * @returns Retrieved bookings settings.
 * @fqn wix.bookings.settings.v2.BookingsSettingsService.GetBookingsSettings
 */
export async function getBookingsSettings(): Promise<
  NonNullablePaths<BookingsSettings, `displayTimeZone.basedOn`, 3> & {
    __applicationErrorsType?: GetBookingsSettingsApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[0] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({});

  const reqOpts =
    ambassadorWixBookingsV2BookingsSettings.getBookingsSettings(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)
      ?.bookingsSettings!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {},
        singleArgumentUnchanged: false,
      },
      []
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Updates the site's bookings settings.
 *
 * Each time the bookings settings are updated, `revision` increments by 1. The current `revision` must be passed when updating the bookings settings. This ensures you're working with the latest bookings settings and prevents unintended overwrites.
 *
 * This is a partial update: only the fields listed in `fieldMask.paths` are changed. Fields not included in the field mask keep their current values.
 *
 * If no settings have been customized for the site yet, the default settings are created and then updated with the values you provide.
 * @param bookingsSettings - Bookings settings to update.
 * @public
 * @documentationMaturity preview
 * @requiredField bookingsSettings
 * @requiredField bookingsSettings.revision
 * @permissionId BOOKINGS.SETTINGS_UPDATE
 * @applicableIdentity APP
 * @returns Updated bookings settings.
 * @fqn wix.bookings.settings.v2.BookingsSettingsService.UpdateBookingsSettings
 */
export async function updateBookingsSettings(
  bookingsSettings: NonNullablePaths<BookingsSettings, `revision`, 2>
): Promise<NonNullablePaths<BookingsSettings, `displayTimeZone.basedOn`, 3>> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    bookingsSettings: bookingsSettings,
  });

  const reqOpts =
    ambassadorWixBookingsV2BookingsSettings.updateBookingsSettings(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)
      ?.bookingsSettings!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { bookingsSettings: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['bookingsSettings']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}
